import { TriangleAlert } from 'lucide-react';
import type { OrcamentoResponse } from '@triade/contracts';
import { temNumerosCusto } from '@/components/orcamento/custo-atualizado';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { formatarFracaoPercentual, formatarPontos } from '@/lib/format';

/**
 * Texto do alerta de custo (cartao do kanban e linha da lista). A API decide quando existe (`alertaCusto`); os numeros so
 * aparecem quando a resposta os traz (`custo.ver`). VENDEDOR e LEITURA leem so "custos mudaram desde o envio".
 */
export function textoAlertaCusto(o: Pick<OrcamentoResponse, 'comparativoCusto'>): string {
  const c = o.comparativoCusto;
  if (c && temNumerosCusto(c)) {
    const limite = c.limiarPp != null ? `${formatarPontos(c.limiarPp)} pp` : 'sem parâmetros vigentes';
    return `Custos mudaram desde o envio: lucro de ${formatarFracaoPercentual(c.noEnvio)} para ${formatarFracaoPercentual(c.hoje)}, queda de ${formatarPontos(c.quedaPp)} pp (limite ${limite}).`;
  }
  return 'Custos mudaram desde o envio: o lucro caiu além do limite.';
}

/** Icone de alerta com tooltip. Foca pelo teclado (tabIndex) e tem nome acessivel (aria-label) com o mesmo texto. */
export function AlertaCusto({ orcamento }: { orcamento: OrcamentoResponse }) {
  if (!orcamento.alertaCusto) return null;
  const texto = textoAlertaCusto(orcamento);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          role="img"
          tabIndex={0}
          aria-label={texto}
          data-testid="alerta-custo"
          className="inline-flex size-5 shrink-0 items-center justify-center rounded-full text-warning-ink outline-none focus-visible:shadow-focus"
        >
          <TriangleAlert aria-hidden="true" className="size-4" />
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-[280px]">{texto}</TooltipContent>
    </Tooltip>
  );
}

/** Legenda do alerta, abaixo da lista e do kanban. Sem numeros: vale para todos os papeis. */
export function LegendaAlertaCusto() {
  return (
    <p data-testid="legenda-alerta-custo" className="flex items-center gap-1.5 text-caption text-ink-muted">
      <TriangleAlert aria-hidden="true" className="size-3.5 text-warning-ink" />
      Alerta de custo: custos mudaram e o lucro caiu além do limite desde o envio (propostas enviadas ou em negociação, dentro da validade).
    </p>
  );
}
